<template>
  <div
    v-if="visible"
    class="import-failures-modal-backdrop"
    role="dialog"
    aria-modal="true"
    aria-labelledby="import-failures-title"
    @click.self="onDismiss"
  >
    <div class="import-failures-modal font-sans text-foreground">
      <div class="import-failures-modal__header">
        <div>
          <h2 id="import-failures-title" class="import-failures-modal__title">
            Import finished with failures
          </h2>
          <p class="import-failures-modal__subtitle mb-0">
            {{ subtitle }}
          </p>
        </div>
        <button type="button" class="btn-close" aria-label="Close" @click="onDismiss"></button>
      </div>

      <div class="import-failures-modal__body">
        <div class="import-failures-modal__stats">
          <span class="text-success">Success: {{ successCount }}</span>
          <span class="text-danger">Failed: {{ failCount }}</span>
          <span class="text-muted text-capitalize">Status: {{ statusLabel }}</span>
        </div>

        <div v-if="loading" class="text-muted py-4 text-center">Loading failed products…</div>
        <div v-else-if="loadError" class="alert alert-warning mb-0">{{ loadError }}</div>
        <div v-else-if="!failures.length" class="text-muted py-3">
          No detailed failure rows were stored for this job.
          <span v-if="failCount > 0">Fail count is {{ failCount }}.</span>
        </div>
        <div v-else class="import-failures-modal__list">
          <div
            v-for="(item, idx) in failures"
            :key="`${item.row_number}-${item.sku}-${idx}`"
            class="import-failures-modal__item"
          >
            <div class="import-failures-modal__item-top">
              <span v-if="item.row_number" class="badge text-bg-secondary">ROW {{ item.row_number }}</span>
              <span v-if="item.sku" class="import-failures-modal__sku">SKU {{ item.sku }}</span>
              <span v-if="item.name" class="import-failures-modal__name">{{ item.name }}</span>
            </div>
            <div class="text-danger">{{ item.message }}</div>
            <div v-if="item.code" class="small text-muted">{{ item.code }}</div>
            <div v-if="item.remediation" class="small text-muted mt-1">
              <strong>Fix:</strong> {{ item.remediation }}
            </div>
          </div>
        </div>
      </div>

      <div class="import-failures-modal__footer">
        <button
          type="button"
          class="btn btn-outline-secondary btn-sm"
          :disabled="exporting || (!failures.length && failCount <= 0)"
          @click="downloadSpreadsheet"
        >
          <span v-if="exporting" class="spinner-border spinner-border-sm me-1" role="status"></span>
          <i v-else class="fas fa-file-excel me-1"></i>
          Download failures spreadsheet
        </button>
        <button
          type="button"
          class="btn btn-primary btn-sm"
          @click="onImportAgain"
        >
          <i class="fas fa-upload me-1"></i>
          Resolve &amp; import again
        </button>
        <button type="button" class="btn btn-link btn-sm text-muted" @click="onDismiss">
          Dismiss
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import api from '@/utils/api'

/**
 * Modal shown when a product import job completes or is cancelled with failures.
 * Download a full Excel of failed rows, or reopen Import Products for the business.
 */
export default {
  name: 'ImportJobFailuresModal',
  props: {
    visible: { type: Boolean, default: false },
    jobId: { type: [Number, String], default: null },
    businessId: { type: [Number, String], default: null },
    businessName: { type: String, default: '' },
    status: { type: String, default: '' },
    successCount: { type: Number, default: 0 },
    failCount: { type: Number, default: 0 },
  },
  emits: ['dismiss', 'import-again'],
  data() {
    return {
      loading: false,
      loadError: null,
      failures: [],
      exporting: false,
    }
  },
  computed: {
    statusLabel() {
      return String(this.status || '').replace(/_/g, ' ') || '—'
    },
    subtitle() {
      const name = (this.businessName || '').trim() || 'this business'
      return `Review failed products for ${name}, download a spreadsheet to fix offline, then import again.`
    },
  },
  watch: {
    visible: {
      immediate: true,
      handler(val) {
        if (val && this.jobId) this.loadFailures()
      },
    },
    jobId(val) {
      if (this.visible && val) this.loadFailures()
    },
  },
  methods: {
    async loadFailures() {
      if (!this.jobId) return
      this.loading = true
      this.loadError = null
      try {
        const { data } = await api.get(`/import-jobs/${this.jobId}/failures/`)
        this.failures = Array.isArray(data?.results) ? data.results : []
      } catch (e) {
        this.loadError =
          e.response?.data?.error ||
          e.response?.data?.detail ||
          e.message ||
          'Failed to load import failures.'
        this.failures = []
      } finally {
        this.loading = false
      }
    },
    async downloadSpreadsheet() {
      if (!this.jobId || this.exporting) return
      this.exporting = true
      try {
        const resp = await api.get(`/import-jobs/${this.jobId}/failures/export/`, {
          responseType: 'blob',
        })
        const raw = resp.data
        const blob =
          raw instanceof Blob
            ? raw
            : new Blob([raw], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
              })
        const head = new Uint8Array(await blob.slice(0, 2).arrayBuffer())
        // Valid xlsx is a ZIP (PK). Text/JSON corruption often starts with `{` or `<`.
        if (head.length < 2 || head[0] !== 0x50 || head[1] !== 0x4b) {
          let detail = 'Download was not a valid Excel file.'
          try {
            detail = (await blob.text()).slice(0, 200) || detail
          } catch (_) {
            /* ignore */
          }
          throw new Error(detail)
        }
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        const disposition = resp.headers?.get?.('content-disposition') || ''
        const match = /filename="?([^"]+)"?/i.exec(disposition)
        a.href = url
        a.download = match?.[1] || `import_job_${this.jobId}_failures.xlsx`
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        console.error('Failed to download import failures spreadsheet', e)
        this.loadError =
          e.response?.data?.error ||
          e.message ||
          'Could not download the failures spreadsheet.'
      } finally {
        this.exporting = false
      }
    },
    onImportAgain() {
      this.$emit('import-again', {
        jobId: this.jobId,
        businessId: this.businessId,
        businessName: this.businessName,
      })
    },
    onDismiss() {
      this.$emit('dismiss')
    },
  },
}
</script>

<style scoped>
.import-failures-modal-backdrop {
  position: fixed;
  inset: 0;
  z-index: 10050;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
}
.import-failures-modal {
  width: min(640px, 100%);
  max-height: min(85vh, 720px);
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 18px 50px rgba(15, 23, 42, 0.28);
  display: flex;
  flex-direction: column;
  overflow: hidden;
}
.import-failures-modal__header {
  display: flex;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.25rem;
  border-bottom: 1px solid rgba(15, 23, 42, 0.08);
}
.import-failures-modal__title {
  font-size: 1.1rem;
  font-weight: 700;
  margin: 0 0 0.25rem;
}
.import-failures-modal__subtitle {
  font-size: 0.85rem;
  color: #64748b;
}
.import-failures-modal__body {
  padding: 1rem 1.25rem;
  overflow: auto;
  flex: 1;
}
.import-failures-modal__stats {
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1.25rem;
  font-size: 0.875rem;
  margin-bottom: 0.75rem;
}
.import-failures-modal__list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.import-failures-modal__item {
  border: 1px solid rgba(15, 23, 42, 0.08);
  border-radius: 8px;
  padding: 0.75rem;
  background: #fafafa;
}
.import-failures-modal__item-top {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: baseline;
  margin-bottom: 0.35rem;
  font-size: 0.85rem;
}
.import-failures-modal__sku {
  font-weight: 600;
}
.import-failures-modal__name {
  color: #334155;
}
.import-failures-modal__footer {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  align-items: center;
  justify-content: flex-end;
  padding: 0.85rem 1.25rem;
  border-top: 1px solid rgba(15, 23, 42, 0.08);
  background: #f8fafc;
}
</style>
